import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectsAPI, companiesAPI, peopleAPI, taskGroupsAPI, tasksAPI } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, RefreshCw, FolderKanban, Info, ChevronRight, ChevronDown, ExternalLink, Layers, Save, Filter, X, User, Lock, History, Loader2, GitBranch, Link2 } from "lucide-react";
import { format, parseISO } from "date-fns";

const STATUS_COLORS = { 
  "Active": "bg-indigo-500", 
  "Completed": "bg-green-500", 
  "On Hold": "bg-amber-500", 
  "Archived": "bg-slate-500",
  "Canceled": "bg-red-600",
  "Cancelled": "bg-red-600",
  "Pre Project Planning": "bg-purple-500"
};
const TASK_STATUS_COLORS = { "Not Started": "bg-slate-500", "In Progress": "bg-blue-500", "Complete": "bg-green-500", "Completed": "bg-green-500", "Overdue": "bg-red-500", "Revision Required": "bg-amber-500", "Skipping": "bg-gray-400" };
const TASK_STATUSES = ["Not Started", "In Progress", "Completed", "Overdue", "Revision Required", "Skipping"];
const PROJECT_STATUSES = ["Active", "Pre Project Planning", "Completed", "On Hold", "Archived", "Canceled"];

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try { return format(parseISO(dateStr), "MMM d, yyyy"); } catch { return "-"; }
}

function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  try { return format(parseISO(dateStr), "yyyy-MM-dd"); } catch { return ""; }
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, canEdit } = useAuth();
  const [projects, setProjects] = useState([]);
  const [allTaskGroups, setAllTaskGroups] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [people, setPeople] = useState({ osr: [], isr: [], engineering: [], management: [], design: [], operations: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [osrFilter, setOsrFilter] = useState("all");
  const [isrFilter, setIsrFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(searchParams.get("status") ? true : false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedTaskGroups, setExpandedTaskGroups] = useState({});
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({});
  const [taskChangelog, setTaskChangelog] = useState([]);
  const [loadingChangelog, setLoadingChangelog] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState({}); // { taskId: true/false }
  const [selectionMode, setSelectionMode] = useState(false);
  const [formData, setFormData] = useState({
    description: "", status: "Active", template_choice: "Task Template 2", project_type: "Custom",
    start_date: "", end_date: "", notes: "", company_id: "", osr_id: "", isr_ids: []
  });

  const fetchTaskChangelog = async (taskId) => {
    setLoadingChangelog(true);
    try {
      const res = await tasksAPI.getChangelog(taskId);
      setTaskChangelog(res.data?.data?.changes || []);
    } catch (e) {
      setTaskChangelog([]);
    }
    setLoadingChangelog(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // If user is not management, filter by their resource_id
      const resourceId = user && !user.is_management && user.resource_id ? user.resource_id : null;
      
      const [projectsRes, taskGroupsRes, tasksRes, companiesRes, peopleRes] = await Promise.all([
        projectsAPI.getAll(resourceId), 
        taskGroupsAPI.getAll(), 
        tasksAPI.getAll(null, resourceId), 
        companiesAPI.getAll(), 
        peopleAPI.getAll()
      ]);
      setProjects(projectsRes.data.data || []);
      setAllTaskGroups(taskGroupsRes.data.data || []);
      setAllTasks(tasksRes.data.data || []);
      setCompanies(companiesRes.data.data || []);
      setPeople(peopleRes.data.data || { osr: [], isr: [] });
    } catch (error) { toast.error("Failed to fetch data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [user]);

  // Memoize filtered projects to avoid recalculation on every render
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const name = project.fields?.["Project Name"] || "";
      const description = project.fields?.["Project Description"] || "";
      const customer = project.fields?.["Customer (from Company Name)"]?.[0] || "";
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = name.toLowerCase().includes(searchLower) || description.toLowerCase().includes(searchLower) || customer.toLowerCase().includes(searchLower);
      
      // Status filter
      const projectStatus = project.fields?.["Project Status"] || "";
      const matchesStatus = statusFilter === "all" || projectStatus === statusFilter;
      
      // OSR filter
      const projectOsr = project.fields?.["OSR"] || [];
      const matchesOsr = osrFilter === "all" || projectOsr.includes(osrFilter);
      
      // ISR filter
      const projectIsr = project.fields?.["ISR"] || [];
      const matchesIsr = isrFilter === "all" || projectIsr.includes(isrFilter);
      
      return matchesSearch && matchesStatus && matchesOsr && matchesIsr;
    });
  }, [projects, searchTerm, statusFilter, osrFilter, isrFilter]);

  const activeFiltersCount = useMemo(() => 
    [statusFilter, osrFilter, isrFilter].filter(f => f !== "all").length,
    [statusFilter, osrFilter, isrFilter]
  );

  const clearFilters = useCallback(() => {
    setStatusFilter("all");
    setOsrFilter("all");
    setIsrFilter("all");
    setSearchTerm("");
  }, []);

  // Memoize task group lookup by project ID
  const taskGroupsByProject = useMemo(() => {
    const map = {};
    allTaskGroups.forEach(tg => {
      const projectIds = tg.fields?.["Project Association"] || [];
      projectIds.forEach(pid => {
        if (!map[pid]) map[pid] = [];
        map[pid].push(tg);
      });
    });
    // Sort each project's task groups
    Object.keys(map).forEach(pid => {
      map[pid].sort((a, b) => (a.fields?.["Task Order"] || 0) - (b.fields?.["Task Order"] || 0));
    });
    return map;
  }, [allTaskGroups]);

  // Memoize tasks lookup by group ID
  const tasksByGroup = useMemo(() => {
    const map = {};
    allTasks.forEach(task => {
      const groupIds = task.fields?.["Task Group"] || [];
      groupIds.forEach(gid => {
        if (!map[gid]) map[gid] = [];
        map[gid].push(task);
      });
    });
    // Sort each group's tasks
    Object.keys(map).forEach(gid => {
      map[gid].sort((a, b) => (a.fields?.["Task Order"] || 0) - (b.fields?.["Task Order"] || 0));
    });
    return map;
  }, [allTasks]);

  const getTaskGroupsForProject = useCallback((projectId) => {
    return taskGroupsByProject[projectId] || [];
  }, [taskGroupsByProject]);

  const getTasksForGroup = useCallback((groupId) => {
    return tasksByGroup[groupId] || [];
  }, [tasksByGroup]);

  // Helper to get resource name by ID
  const getResourceName = (resourceId) => {
    const allResources = [...(people.osr || []), ...(people.isr || [])];
    const resource = allResources.find(r => r.id === resourceId);
    return resource?.name || '';
  };

  // Helper to get OSR/ISR names for a project
  const getProjectOsrNames = (project) => {
    const osrIds = project.fields?.["OSR"] || [];
    return osrIds.map(id => getResourceName(id)).filter(Boolean).join(', ');
  };

  const getProjectIsrNames = (project) => {
    const isrIds = project.fields?.["ISR"] || [];
    return isrIds.map(id => getResourceName(id)).filter(Boolean).join(', ');
  };

  // Helper to get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || '';
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleTaskGroup = (groupId) => {
    setExpandedTaskGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const openEditTask = async (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.fields?.["Task Name"] || "",
      status: task.fields?.Status || "Not Started",
      start_date: formatDateForInput(task.fields?.["Start Date"]),
      due_date: formatDateForInput(task.fields?.["Due Date"]),
      duration: task.fields?.["Task Duration"] || 0,
      role: task.fields?.Role || "",
      assigned_to: task.fields?.["Assigned To"] || [],
      dates_locked: task.fields?.dates_locked || false,
      predecessors: task.fields?.predecessors || []
    });
    // Fetch changelog for this task
    fetchTaskChangelog(task.id);
    // Fetch available predecessors
    fetchAvailablePredecessors(task.id, task.fields?.Projects?.[0]);
  };

  const [availablePredecessors, setAvailablePredecessors] = useState([]);
  const [loadingPredecessors, setLoadingPredecessors] = useState(false);

  const fetchAvailablePredecessors = async (taskId, projectId) => {
    setLoadingPredecessors(true);
    try {
      const res = await tasksAPI.getAvailablePredecessors(taskId, projectId);
      setAvailablePredecessors(res.data?.data?.available_predecessors || []);
    } catch (e) {
      console.error("Failed to fetch predecessors:", e);
      setAvailablePredecessors([]);
    }
    setLoadingPredecessors(false);
  };

  // Dependency impact dialog state
  const [dependencyImpactDialog, setDependencyImpactDialog] = useState({
    open: false,
    dependentTasks: [],
    pendingTaskUpdate: null
  });

  const saveTask = async () => {
    try {
      // Check if dates changed
      const originalTask = editingTask.fields || {};
      const datesChanged = 
        taskForm.start_date !== formatDateForInput(originalTask["Start Date"]) ||
        taskForm.due_date !== formatDateForInput(originalTask["Due Date"]);
      
      if (datesChanged) {
        // Check if any tasks depend on this one
        const impactRes = await tasksAPI.checkDependencyImpact(editingTask.id, []);
        const impact = impactRes.data?.data;
        
        if (impact?.has_dependents && impact?.tasks_needing_shift > 0) {
          // Show confirmation dialog
          setDependencyImpactDialog({
            open: true,
            dependentTasks: impact.dependent_tasks.filter(t => t.needs_shift),
            pendingTaskUpdate: { ...taskForm }
          });
          return; // Don't save yet, wait for user decision
        }
      }
      
      // No dependencies affected, save directly
      await tasksAPI.update(editingTask.id, taskForm);
      toast.success("Task updated");
      setEditingTask(null);
      fetchData();
    } catch (e) { 
      console.error("Save task error:", e);
      toast.error("Failed to update task"); 
    }
  };

  const handleDependencyImpactConfirm = async (shiftDependents) => {
    try {
      const pendingUpdate = dependencyImpactDialog.pendingTaskUpdate;
      
      // Save the original task
      await tasksAPI.update(editingTask.id, pendingUpdate);
      
      if (shiftDependents && dependencyImpactDialog.dependentTasks.length > 0) {
        // Calculate the new start date for dependents (day after the new due date)
        const newDueDate = pendingUpdate.due_date;
        if (newDueDate) {
          const dueDate = new Date(newDueDate);
          dueDate.setDate(dueDate.getDate() + 1);
          // Skip weekends
          while (dueDate.getDay() === 0 || dueDate.getDay() === 6) {
            dueDate.setDate(dueDate.getDate() + 1);
          }
          const newStartDate = dueDate.toISOString().split('T')[0];
          
          // Update each dependent task's start date
          for (const dep of dependencyImpactDialog.dependentTasks) {
            await tasksAPI.update(dep.id, { start_date: newStartDate });
          }
          toast.success(`Updated ${dependencyImpactDialog.dependentTasks.length + 1} task(s)`);
        }
      } else {
        toast.success("Task updated (dependent tasks unchanged)");
      }
      
      setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null });
      setEditingTask(null);
      fetchData();
    } catch (e) {
      console.error("Dependency impact confirm error:", e);
      toast.error("Failed to update tasks");
    }
  };

  const quickUpdateTaskStatus = async (task, newStatus) => {
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      toast.success("Status updated");
      fetchData();
    } catch (e) { toast.error("Failed to update status"); }
  };

  // Parallel group management
  const toggleTaskSelection = (taskId, e) => {
    e?.stopPropagation();
    setSelectedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getSelectedTaskIds = () => Object.entries(selectedTasks).filter(([, selected]) => selected).map(([id]) => id);

  const clearSelection = () => {
    setSelectedTasks({});
    setSelectionMode(false);
  };

  const createParallelGroup = async () => {
    const taskIds = getSelectedTaskIds();
    if (taskIds.length < 2) {
      toast.error("Select at least 2 tasks to create a parallel group");
      return;
    }
    try {
      await tasksAPI.createParallelGroup(taskIds);
      toast.success(`Created parallel group with ${taskIds.length} tasks`);
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create parallel group");
    }
  };

  const removeFromParallelGroup = async () => {
    const taskIds = getSelectedTaskIds();
    if (taskIds.length === 0) {
      toast.error("Select at least 1 task to remove from parallel group");
      return;
    }
    try {
      await tasksAPI.removeParallelGroup(taskIds);
      toast.success("Removed tasks from parallel group");
      clearSelection();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove from parallel group");
    }
  };

  const openCreateDialog = () => {
    setSelectedProject(null);
    setFormData({ description: "", status: "Active", template_choice: "Task Template 2", project_type: "Custom", start_date: "", end_date: "", notes: "", company_id: "", osr_id: "", isr_ids: [] });
    setDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setSelectedProject(project);
    setFormData({
      description: project.fields?.["Project Description"] || "", status: project.fields?.["Project Status"] || "Active",
      template_choice: project.fields?.["Template Choice"] || "Task Template 2", project_type: project.fields?.["Project Type"] || "Custom",
      start_date: project.fields?.["Project Start Date"] || "", end_date: project.fields?.["Project End Date"] || "",
      notes: project.fields?.["Notes"] || "", company_id: project.fields?.["Company Name"]?.[0] || "",
      osr_id: project.fields?.["OSR"]?.[0] || "", isr_ids: project.fields?.["ISR"] || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Transform form data to match backend expectations
      const submitData = {
        ...formData,
        osr_ids: formData.osr_id ? [formData.osr_id] : [],
        isr_ids: formData.isr_ids || [],
      };
      // Remove the singular osr_id field
      delete submitData.osr_id;
      
      console.log("Submitting project data:", submitData);
      
      if (selectedProject) {
        await projectsAPI.update(selectedProject.id, submitData);
        toast.success("Project updated");
      } else {
        const result = await projectsAPI.create(submitData);
        console.log("Create result:", result);
        toast.success("Project created with auto-generated tasks!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) { 
      console.error("Submit error:", error);
      toast.error(selectedProject ? "Failed to update" : "Failed to create"); 
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.error("Please enter a company name");
      return;
    }
    try {
      const result = await companiesAPI.create({ name: newCompanyName.trim() });
      if (result.data.success) {
        toast.success("Company created!");
        setFormData({ ...formData, company_id: result.data.data.id });
        setNewCompanyDialogOpen(false);
        setNewCompanyName("");
        // Refresh companies list
        const companiesRes = await companiesAPI.getAll();
        setCompanies(companiesRes.data.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create company");
    }
  };

  const handleDelete = async () => {
    try {
      await projectsAPI.delete(selectedProject.id);
      toast.success("Project deleted");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) { toast.error("Failed to delete"); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">Click projects to expand task groups, click task groups to see and edit tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button onClick={openCreateDialog} data-testid="create-project-btn" disabled={!canEdit}>
                    {!canEdit && <Lock className="h-4 w-4 mr-2" />}
                    {canEdit && <Plus className="h-4 w-4 mr-2" />}
                    New Project
                  </Button>
                </span>
              </TooltipTrigger>
              {!canEdit && (
                <TooltipContent>
                  <p>Management access required</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search projects..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="project-search" />
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant={showFilters ? "secondary" : "outline"} 
                  size="sm" 
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative"
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">{activeFiltersCount}</Badge>
                  )}
                </Button>
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <Badge variant="secondary">{filteredProjects.length} projects</Badge>
              </div>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg" data-testid="filters-panel">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {PROJECT_STATUSES.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">OSR</Label>
                  <Select value={osrFilter} onValueChange={setOsrFilter}>
                    <SelectTrigger data-testid="osr-filter">
                      <SelectValue placeholder="All OSRs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All OSRs</SelectItem>
                      {people.osr?.map(person => (
                        <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">ISR</Label>
                  <Select value={isrFilter} onValueChange={setIsrFilter}>
                    <SelectTrigger data-testid="isr-filter">
                      <SelectValue placeholder="All ISRs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All ISRs</SelectItem>
                      {people.isr?.map(person => (
                        <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredProjects.map((project) => {
              const isProjectExpanded = expandedProjects[project.id];
              const projectTaskGroups = getTaskGroupsForProject(project.id);
              const progress = (project.fields?.["% of project completed"] || 0) * 100;
              
              return (
                <div key={project.id} className="border rounded-lg overflow-hidden">
                  {/* Project Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleProject(project.id)}
                    onDoubleClick={(e) => { e.stopPropagation(); openEditDialog(project); }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {isProjectExpanded ? <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
                      <FolderKanban className="h-5 w-5 text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium">{project.fields?.["Project Name"] || "Untitled"}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {project.fields?.["Customer (from Company Name)"]?.[0] || getCompanyName(project.fields?.["Company Name"]?.[0]) || project.fields?.["Project Description"] || ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* OSR/ISR badges */}
                      <div className="hidden lg:flex items-center gap-2">
                        {getProjectOsrNames(project) && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            OSR: {getProjectOsrNames(project)}
                          </Badge>
                        )}
                        {getProjectIsrNames(project) && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            ISR: {getProjectIsrNames(project)}
                          </Badge>
                        )}
                      </div>
                      <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatDate(project.fields?.["Project Start Date"])}</span>
                        <span>→</span>
                        <span>{formatDate(project.fields?.["Project End Date"])}</span>
                      </div>
                      <div className="hidden sm:flex items-center gap-2">
                        <div className="w-20 h-2 bg-muted rounded-full"><div className="h-full bg-primary rounded-full" style={{ width: `${progress}%` }} /></div>
                        <span className="text-xs w-10">{progress.toFixed(0)}%</span>
                      </div>
                      <Badge variant="secondary">{projectTaskGroups.length} groups</Badge>
                      <Badge className={STATUS_COLORS[project.fields?.["Project Status"]] || "bg-slate-500"}>{project.fields?.["Project Status"] || "Unknown"}</Badge>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${project.id}`)}><ExternalLink className="h-4 w-4" /></Button>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(project)} disabled={!canEdit}><Pencil className="h-4 w-4" /></Button>
                              </span>
                            </TooltipTrigger>
                            {!canEdit && <TooltipContent><p>Management access required</p></TooltipContent>}
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => { setSelectedProject(project); setDeleteDialogOpen(true); }} disabled={!canEdit}><Trash2 className="h-4 w-4" /></Button>
                              </span>
                            </TooltipTrigger>
                            {!canEdit && <TooltipContent><p>Management access required</p></TooltipContent>}
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Project Content - Task Groups */}
                  {isProjectExpanded && (
                    <div className="border-t bg-muted/20 p-4">
                      <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Task Groups ({projectTaskGroups.length}) - Click to expand and edit tasks
                      </div>
                      {projectTaskGroups.length > 0 ? (
                        <div className="space-y-2">
                          {projectTaskGroups.map((tg) => {
                            const isGroupExpanded = expandedTaskGroups[tg.id];
                            const groupTasks = getTasksForGroup(tg.id);
                            const groupProgress = (tg.fields?.["Progress %"] || 0) * 100;
                            
                            return (
                              <div key={tg.id} className="border rounded-lg bg-background">
                                {/* Task Group Header */}
                                <div 
                                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); toggleTaskGroup(tg.id); }}
                                >
                                  <div className="flex items-center gap-2">
                                    {isGroupExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                    <span className="text-sm font-medium">{tg.fields?.["Task Group Name"] || "Unnamed"}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground hidden sm:inline">
                                      {formatDate(tg.fields?.["Task Group Start Date"])} → {formatDate(tg.fields?.["Task Group Due Date"])}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <div className="w-12 h-1.5 bg-muted rounded-full"><div className="h-full bg-primary rounded-full" style={{ width: `${groupProgress}%` }} /></div>
                                      <span className="text-xs w-8">{groupProgress.toFixed(0)}%</span>
                                    </div>
                                    <Badge variant="secondary" className={`text-xs ${TASK_STATUS_COLORS[tg.fields?.["Progress Status"]] || ""}`}>
                                      {tg.fields?.["Progress Status"] || "Not Started"}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">{groupTasks.length} tasks</Badge>
                                  </div>
                                </div>

                                {/* Expanded Task Group - Tasks List */}
                                {isGroupExpanded && (
                                  <div className="border-t bg-muted/10 p-3">
                                    {/* Selection toolbar */}
                                    {selectionMode && getSelectedTaskIds().length > 0 && (
                                      <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                          {getSelectedTaskIds().length} selected
                                        </span>
                                        <div className="flex-1" />
                                        <Button size="sm" variant="outline" onClick={createParallelGroup} disabled={getSelectedTaskIds().length < 2}>
                                          <GitBranch className="h-3 w-3 mr-1" /> Make Parallel
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={removeFromParallelGroup}>
                                          Remove Parallel
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={clearSelection}>
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )}
                                    {/* Selection mode toggle */}
                                    {canEdit && !selectionMode && (
                                      <div className="flex justify-end mb-2">
                                        <Button size="sm" variant="ghost" onClick={() => setSelectionMode(true)} className="text-xs">
                                          <GitBranch className="h-3 w-3 mr-1" /> Select for Parallel
                                        </Button>
                                      </div>
                                    )}
                                    {groupTasks.length > 0 ? (
                                      <div className="space-y-2">
                                        {(() => {
                                          // Group tasks by parallel_group for visual indication
                                          let currentParallelGroup = null;
                                          let parallelGroupIndex = 0;
                                          const parallelGroupColors = ['border-l-purple-500', 'border-l-cyan-500', 'border-l-pink-500', 'border-l-orange-500'];
                                          
                                          return groupTasks.map((task, taskIndex) => {
                                            const rawStatus = task.fields?.Status || "Not Started";
                                            const isCompleted = rawStatus === "Completed" || rawStatus === "Complete" || rawStatus === "Skipping";
                                            const dueDate = task.fields?.["Due Date"];
                                            const isOverdue = !isCompleted && dueDate && new Date(dueDate) < new Date(new Date().toDateString()) && rawStatus !== "Overdue" && rawStatus !== "Revision Required";
                                            const status = isOverdue ? "Overdue" : rawStatus;
                                            const parallelGroup = task.fields?.parallel_group;
                                            const isSelected = selectedTasks[task.id];
                                            
                                            // Track parallel group transitions for coloring
                                            if (parallelGroup !== currentParallelGroup) {
                                              if (parallelGroup) parallelGroupIndex++;
                                              currentParallelGroup = parallelGroup;
                                            }
                                            const parallelColor = parallelGroup ? parallelGroupColors[parallelGroupIndex % parallelGroupColors.length] : '';
                                            
                                            return (
                                              <div 
                                                key={task.id} 
                                                className={`flex items-center justify-between p-2 rounded border bg-background cursor-pointer hover:bg-muted/50 transition-colors ${isCompleted ? "opacity-50" : ""} ${parallelGroup ? `border-l-4 ${parallelColor}` : ""} ${isSelected ? "ring-2 ring-blue-500" : ""}`}
                                                onDoubleClick={() => !selectionMode && openEditTask(task)}
                                                onClick={() => selectionMode && toggleTaskSelection(task.id)}
                                              >
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                  {selectionMode && (
                                                    <Checkbox 
                                                      checked={isSelected || false}
                                                      onCheckedChange={() => toggleTaskSelection(task.id)}
                                                      onClick={(e) => e.stopPropagation()}
                                                    />
                                                  )}
                                                  {parallelGroup && (
                                                    <TooltipProvider>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <GitBranch className="h-3 w-3 text-purple-500 flex-shrink-0" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          <p>Parallel Group: {parallelGroup}</p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                  {(task.fields?.predecessors?.length > 0) && (
                                                    <TooltipProvider>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <Link2 className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                          <p>{task.fields.predecessors.length} predecessor(s)</p>
                                                        </TooltipContent>
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                  {task.fields?.dates_locked && (
                                                    <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" title="Dates locked" />
                                                  )}
                                                  <span className={`text-sm truncate ${isCompleted ? "line-through" : ""}`}>
                                                    {task.fields?.["Task Name"] || "Unnamed Task"}
                                                  </span>
                                                  {/* Assigned To Badge */}
                                                  {(() => {
                                                    const assignedTo = task.fields?.["Assigned To"] || [];
                                                    if (assignedTo.length === 0) return null;
                                                    const names = assignedTo.map(id => {
                                                      const person = people.all?.find(p => p.id === id);
                                                      return person?.name || null;
                                                    }).filter(Boolean);
                                                    if (names.length === 0) return null;
                                                    return (
                                                      <Badge variant="outline" className="text-xs hidden lg:flex items-center gap-1">
                                                        <User className="h-3 w-3" />
                                                        {names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]}
                                                      </Badge>
                                                    );
                                                  })()}
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                  <span className="text-xs text-muted-foreground hidden md:inline">
                                                    {formatDate(task.fields?.["Start Date"])} - {formatDate(task.fields?.["Due Date"])}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground hidden sm:inline">
                                                    {task.fields?.["Task Duration"] || 0}d
                                                  </span>
                                                  <TooltipProvider>
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <span>
                                                          <Select value={rawStatus} onValueChange={(val) => quickUpdateTaskStatus(task, val)} disabled={!canEdit || selectionMode}>
                                                            <SelectTrigger className={`h-7 w-28 ${!canEdit ? 'opacity-70' : ''}`}>
                                                              <Badge className={`text-xs ${TASK_STATUS_COLORS[status] || "bg-slate-500"}`}>{status}</Badge>
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                              {TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                                            </SelectContent>
                                                          </Select>
                                                        </span>
                                                      </TooltipTrigger>
                                                      {!canEdit && <TooltipContent><p>Management access required</p></TooltipContent>}
                                                    </Tooltip>
                                                  </TooltipProvider>
                                                  {!selectionMode && (
                                                    <TooltipProvider>
                                                      <Tooltip>
                                                        <TooltipTrigger asChild>
                                                          <span>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditTask(task)} disabled={!canEdit}>
                                                              <Pencil className="h-3 w-3" />
                                                            </Button>
                                                          </span>
                                                        </TooltipTrigger>
                                                        {!canEdit && <TooltipContent><p>Management access required</p></TooltipContent>}
                                                      </Tooltip>
                                                    </TooltipProvider>
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          });
                                        })()}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-muted-foreground text-center py-4">No tasks in this group</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-muted-foreground">No task groups</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit Task Dialog */}
      <Dialog open={!!editingTask} onOpenChange={(open) => { if (!open) setEditingTask(null); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update task details, dependencies, and view change history</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="dependencies" className="flex items-center gap-1">
                <Link2 className="h-3 w-3" /> Dependencies
              </TabsTrigger>
              <TabsTrigger value="changelog" className="flex items-center gap-1">
                <History className="h-3 w-3" /> History
              </TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Name</Label>
                <Input value={taskForm.title || ""} onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={taskForm.status} onValueChange={(v) => setTaskForm({...taskForm, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (days)</Label>
                  <Input type="number" value={taskForm.duration || 0} onChange={(e) => setTaskForm({...taskForm, duration: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={taskForm.start_date || ""} onChange={(e) => setTaskForm({...taskForm, start_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input type="date" value={taskForm.due_date || ""} onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})} />
                </div>
              </div>
              <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <Checkbox 
                  id="dates-locked" 
                  checked={taskForm.dates_locked || false}
                  onCheckedChange={(checked) => setTaskForm({...taskForm, dates_locked: checked})}
                />
                <div className="grid gap-0.5 leading-none">
                  <Label htmlFor="dates-locked" className="text-sm font-medium cursor-pointer">
                    🔒 Lock Dates
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent date cascade from changing this task's dates
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Input value={taskForm.role || ""} onChange={(e) => setTaskForm({...taskForm, role: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select 
                  value={taskForm.assigned_to?.[0] || "none"} 
                  onValueChange={(v) => setTaskForm({...taskForm, assigned_to: v === "none" ? [] : [v]})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {people.all?.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="dependencies" className="py-4">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Link2 className="h-4 w-4" /> Predecessors (Must Complete First)
                  </Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    This task cannot start until all selected predecessor tasks are completed.
                  </p>
                  {loadingPredecessors ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <ScrollArea className="h-[250px] rounded-md border p-2">
                      {availablePredecessors.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                          <Link2 className="h-8 w-8 mb-2 opacity-50" />
                          <p className="text-sm">No available predecessor tasks</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {availablePredecessors.map((pred) => {
                            const isSelected = (taskForm.predecessors || []).includes(pred.id);
                            const isCompleted = pred.status === "Completed" || pred.status === "Complete";
                            return (
                              <div
                                key={pred.id}
                                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' : ''}`}
                                onClick={() => {
                                  const currentPreds = taskForm.predecessors || [];
                                  if (isSelected) {
                                    setTaskForm({...taskForm, predecessors: currentPreds.filter(id => id !== pred.id)});
                                  } else {
                                    setTaskForm({...taskForm, predecessors: [...currentPreds, pred.id]});
                                  }
                                }}
                              >
                                <Checkbox checked={isSelected} className="pointer-events-none" />
                                <span className="text-xs text-muted-foreground w-6 flex-shrink-0">#{pred.order || 0}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm truncate ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                      {pred.name}
                                    </span>
                                    {isCompleted && <Badge variant="outline" className="text-xs bg-green-100 text-green-700">Done</Badge>}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {pred.project && `${pred.project} • `}{pred.task_group || 'No group'} • Due: {pred.due_date || 'Not set'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  )}
                  {(taskForm.predecessors || []).length > 0 && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-md">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>{(taskForm.predecessors || []).length}</strong> predecessor(s) selected. 
                        This task will be blocked until they complete.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="changelog" className="py-4">
              <ScrollArea className="h-[300px] rounded-md border">
                {loadingChangelog ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : taskChangelog.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <History className="h-8 w-8 mb-2" />
                    <p>No changes recorded yet</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {taskChangelog.map((change, idx) => {
                      const changeDate = change.changed_at ? new Date(change.changed_at) : null;
                      const formattedDate = changeDate ? format(changeDate, "MMM d, yyyy h:mm a") : "Unknown";
                      const changeTypeColors = {
                        "status_change": "bg-blue-100 text-blue-700",
                        "assignment": "bg-purple-100 text-purple-700",
                        "date_change": "bg-amber-100 text-amber-700",
                        "update": "bg-gray-100 text-gray-700"
                      };
                      const badgeClass = changeTypeColors[change.change_type] || changeTypeColors.update;
                      
                      return (
                        <div key={idx} className="border-l-2 border-muted pl-3 pb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className={badgeClass + " text-xs"}>
                              {change.field_name}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{formattedDate}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground line-through mr-2">{change.old_value || "(empty)"}</span>
                            <span className="text-foreground">→ {change.new_value || "(empty)"}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            by {change.changed_by}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
            <Button onClick={saveTask}><Save className="h-4 w-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProject ? "Edit Project" : "Create New Project"}</DialogTitle>
            <DialogDescription>{selectedProject ? "Update the project details" : "Fill in the details to create a new project"}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {!selectedProject && (
                <div className="flex items-start gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>Tasks and task groups will be automatically created based on the template.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Project Description *</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pre Project Planning">Pre Project Planning</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                      <SelectItem value="Canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!selectedProject && (
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select value={formData.template_choice} onValueChange={(v) => setFormData({...formData, template_choice: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Task Template 2">Task Template 2</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Customer</Label>
                <Select value={formData.company_id || "none"} onValueChange={(v) => setFormData({...formData, company_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="none">None</SelectItem>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.city || c.state ? ` (${[c.city, c.state].filter(Boolean).join(', ')})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => setNewCompanyDialogOpen(true)}>
                  + New Customer
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>OSR</Label>
                  <Select value={formData.osr_id || "none"} onValueChange={(v) => setFormData({...formData, osr_id: v === "none" ? "" : v})}>
                    <SelectTrigger><SelectValue placeholder="Select OSR" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {people.osr?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ISR</Label>
                  <Select value={formData.isr_ids?.[0] || "none"} onValueChange={(v) => setFormData({...formData, isr_ids: v === "none" ? [] : [v]})}>
                    <SelectTrigger><SelectValue placeholder="Select ISR" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {people.isr?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{selectedProject ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Company Dialog */}
      <Dialog open={newCompanyDialogOpen} onOpenChange={setNewCompanyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input 
                value={newCompanyName} 
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Enter customer name"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCompany(); } }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setNewCompanyDialogOpen(false); setNewCompanyName(""); }}>Cancel</Button>
            <Button onClick={handleCreateCompany}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{selectedProject?.fields?.["Project Name"]}"? This will delete all associated task groups and tasks.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dependency Impact Warning Dialog */}
      <AlertDialog open={dependencyImpactDialog.open} onOpenChange={(open) => !open && setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null })}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-500" />
              Dependent Tasks Found
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  The following {dependencyImpactDialog.dependentTasks.length} task(s) depend on this task and may need their dates adjusted:
                </p>
                <div className="max-h-40 overflow-y-auto rounded-md border p-2 bg-muted/30">
                  {dependencyImpactDialog.dependentTasks.map((dep, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 text-sm">
                      <span className="truncate flex-1">{dep.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {dep.current_start}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-sm font-medium">
                  Would you like to shift the dependent tasks' start dates to follow the new due date?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setDependencyImpactDialog({ open: false, dependentTasks: [], pendingTaskUpdate: null })}>
              Cancel
            </AlertDialogCancel>
            <Button variant="outline" onClick={() => handleDependencyImpactConfirm(false)}>
              Save Without Shifting
            </Button>
            <Button onClick={() => handleDependencyImpactConfirm(true)} className="bg-amber-600 hover:bg-amber-700">
              Shift Dependent Tasks
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
